import { html, css } from 'lit'
import { iosDetectorService } from '../services/ios-detector.service.js'
import { modalStackRegistry } from '../registries/modal-stack-registry.js'

/**
 * Mixin que padroniza o comportamento de fechamento de modais.
 *
 * Fornece:
 * - Propriedade reativa `open` (Boolean, reflect: true)
 * - Método `close()` idempotente que seta `open = false` e dispara `modal-closed`
 * - Integração com History API (pushState ao abrir, popstate para fechar)
 * - Botão de voltar iOS condicional (renderizado apenas em dispositivos iOS)
 *
 * O mixin segue o padrão de composição do projeto (como ProtectedPageMixin),
 * permitindo que subclasses sobrescrevam `close()` chamando `super.close()`
 * para preservar o comportamento base junto com lógica específica.
 *
 * @param {typeof import('lit').LitElement} superClass - Classe base Lit Element
 * @returns {typeof import('lit').LitElement} Classe com comportamento de modal padronizado
 *
 * @example
 * import { ModalBaseMixin } from '../core/mixins/modal-base.mixin.js'
 *
 * class MeuModal extends ModalBaseMixin(LitElement) {
 *   close () {
 *     // Lógica específica do modal
 *     this._resetEstado()
 *     super.close()
 *   }
 * }
 */
export const ModalBaseMixin = (superClass) => class ModalBase extends superClass {
  static properties = {
    ...superClass.properties,
    open: { type: Boolean, reflect: true }
  }

  /**
   * Handler bound do evento popstate para remoção no disconnectedCallback.
   * Armazena a referência da função para permitir removeEventListener correto.
   *
   * @type {Function|null}
   */
  _boundPopstateHandler = null

  /**
   * Flag que indica se um pushState foi realizado para este modal.
   * Evita pushState duplicado e controla se o popstate deve acionar close().
   *
   * @type {boolean}
   */
  _historyStatePushed = false

  /**
   * Flag que indica se uma limpeza de histórico órfão está em andamento.
   *
   * Quando o modal é fechado por trigger direto (backdrop click, botão fechar),
   * é necessário executar history.back() para remover a entrada órfã do histórico.
   * Esse history.back() dispara um evento popstate que NÃO deve acionar close()
   * novamente. Esta flag previne esse re-trigger, evitando loops infinitos.
   *
   * @type {boolean}
   */
  _isCleaningHistory = false

  /**
   * Flag que indica se este modal está sendo fechado como parte de uma cascata.
   *
   * Quando um modal não-topo é fechado programaticamente, todos os modais acima
   * dele na stack devem ser fechados na ordem LIFO (do topo para baixo).
   * Esta flag previne que os modais sendo fechados na cascata disparem
   * uma nova cascata, evitando recursão infinita.
   *
   * @type {boolean}
   */
  _cascadeClose = false

  /**
   * Fecha o modal de forma idempotente, com suporte a cascata de fechamento.
   *
   * Operações realizadas (nesta ordem):
   * 1. Guard de idempotência: se `open` já é `false`, retorna sem efeito
   * 2. Cascata (Req 7.4): se este modal NÃO é o topo da stack e NÃO está
   *    sendo fechado como parte de uma cascata, fecha todos os modais acima
   *    na ordem LIFO (do topo para baixo) antes de fechar este modal
   * 3. Captura se havia histórico pendente (_historyStatePushed) antes de resetar
   * 4. Seta `open = false`
   * 5. Reseta `_historyStatePushed = false`
   * 6. Dispara evento `modal-closed` com bubbles e composed
   * 7. Remove o modal do ModalStackRegistry
   * 8. Se fechamento foi por trigger direto (não popstate) e havia histórico
   *    pendente, executa _cleanupOrphanHistory() para remover entrada órfã
   *
   * O guard previne múltiplos disparos em cenários de fechamento
   * concorrente (ex: popstate + backdrop click simultâneos).
   *
   * A cascata de fechamento (Req 7.4) garante que ao fechar um modal
   * que não é o topo da stack, todos os modais acima dele sejam fechados
   * primeiro na ordem LIFO. A flag _cascadeClose previne recursão infinita:
   * modais fechados como parte da cascata não disparam nova cascata.
   *
   * Quando o fechamento é via popstate, o navegador já consumiu a entrada
   * do histórico, então NÃO é necessário chamar history.back() (Req 3.3).
   * Quando o fechamento é por trigger direto (backdrop, botão, programático),
   * a entrada do histórico fica órfã e precisa ser removida (Req 5.2).
   *
   * Subclasses podem sobrescrever chamando `super.close()` para preservar
   * o comportamento base junto com lógica específica (reset de estado, etc).
   *
   * @param {object} [options={}] - Opções de fechamento
   * @param {boolean} [options.fromPopstate=false] - Indica se o fechamento
   *   foi acionado pelo handler de popstate. Quando true, não executa
   *   limpeza de histórico órfão (o navegador já consumiu a entrada).
   * @fires modal-closed - Evento customizado com bubbles: true, composed: true
   */
  close ({ fromPopstate = false } = {}) {
    if (!this.open) return

    // Cascata de fechamento (Req 7.4):
    // Se este modal não é o topo da stack e não está sendo fechado como parte
    // de uma cascata já em andamento, fecha todos os modais acima dele primeiro
    // na ordem LIFO (do topo para baixo). Cada modal fechado cuida de sua
    // própria limpeza de histórico e remoção da stack.
    if (!this._cascadeClose && !modalStackRegistry.isTop(this)) {
      const modaisAcima = modalStackRegistry.getAbove(this)
      for (const modal of modaisAcima) {
        modal._cascadeClose = true
        modal.close()
        modal._cascadeClose = false
      }
    }

    // Captura estado antes de resetar para decidir se precisa limpar histórico
    const hadHistoryState = this._historyStatePushed

    this.open = false
    this._historyStatePushed = false

    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }))

    modalStackRegistry.unregister(this)

    // Se fechamento foi por trigger direto e havia entrada no histórico,
    // remove a entrada órfã via history.back() (Req 5.2)
    if (!fromPopstate && hadHistoryState) {
      this._cleanupOrphanHistory()
    }
  }

  /**
   * Remove a entrada órfã do histórico quando o modal é fechado por trigger direto.
   *
   * Quando o modal é fechado por backdrop click, botão fechar ou chamada
   * programática (não via popstate), a entrada adicionada pelo pushState
   * na abertura permanece no histórico. Essa entrada "órfã" causaria
   * navegação indesejada se o usuário usasse o botão voltar depois.
   *
   * O método seta _isCleaningHistory = true antes de chamar history.back()
   * para que o handler de popstate (que será disparado pelo history.back())
   * identifique que é uma limpeza e ignore o evento sem acionar close()
   * novamente (Req 5.3 — previne re-trigger e loops infinitos).
   *
   * Fluxo:
   * 1. _isCleaningHistory = true
   * 2. history.back() → dispara popstate
   * 3. Handler de popstate vê _isCleaningHistory === true
   * 4. Handler reseta _isCleaningHistory = false e retorna (sem close())
   *
   * @private
   */
  _cleanupOrphanHistory () {
    this._isCleaningHistory = true
    modalStackRegistry._cleanupInProgress = true
    window.history.back()
  }

  /**
   * Renderiza o botão de voltar iOS no header do modal.
   *
   * Retorna template vazio (`html```) se o dispositivo não for iOS,
   * garantindo que o botão não seja renderizado no DOM em outras plataformas.
   * Quando iOS, renderiza um botão com ícone chevron-left, área de toque
   * mínima de 44x44px (WCAG 2.5.5) e estilo visual consistente com iOS.
   *
   * @returns {import('lit').TemplateResult} Template do botão ou vazio
   */
  renderBotaoVoltarIOS () {
    if (!iosDetectorService.isIOS()) {
      return html``
    }

    return html`
      <button
        class="modal-base-btn-voltar-ios"
        data-cy="btn-voltar-ios"
        aria-label="Voltar"
        @click="${() => this.close()}"
        style="
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 48px;
          min-height: 48px;
          aspect-ratio: 1;
          flex-shrink: 0;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          color: white;
          font-size: 1.1rem;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        "
      >
        <i class="fas fa-chevron-left"></i>
      </button>
    `
  }

  /**
   * Renderiza o botão de fechar (X) no header do modal.
   *
   * Visível apenas em desktop (≥1024px). Em mobile/tablet, o usuário
   * usa o botão voltar iOS (←) ou o botão voltar do sistema Android.
   * Usa a classe Bulma `is-hidden-touch` para ocultar em viewports ≤1024px.
   *
   * Botão quadrado com ícone X (fa-times), posicionado à direita do header.
   * Usa o mesmo padrão visual do botão voltar iOS: quadrado, área de toque
   * adequada (WCAG 2.5.5), sem background, cor branca para contraste.
   *
   * @param {string} dataCy - Valor do atributo data-cy para testes E2E
   * @returns {import('lit').TemplateResult} Template do botão fechar
   */
  renderBotaoFechar (dataCy = 'modal-close') {
    return html`
      <button
        class="modal-base-btn-fechar"
        data-cy="${dataCy}"
        aria-label="Fechar"
        @click="${() => this.close()}"
        style="
          align-items: center;
          justify-content: center;
          min-width: 48px;
          min-height: 48px;
          aspect-ratio: 1;
          flex-shrink: 0;
          padding: 0;
          margin: 0;
          margin-left: auto;
          border: none;
          background: none;
          color: white;
          font-size: 1.1rem;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        "
      >
        <i class="fas fa-times"></i>
      </button>
    `
  }

  /**
   * Estilos do botão de voltar iOS.
   *
   * Replica o posicionamento do botão voltar nativo do iOS:
   * - margin-left: 12px (espaçamento da borda esquerda, similar ao iOS nativo ~16pt)
   * - margin-right: 4px (gap sutil entre botão e título)
   * - font-size: 1.125rem (chevron proporcional ao header, não exagerado)
   * - Sem background, cor primária do design system
   * - Área de toque mínima 44x44px conforme WCAG 2.5.5
   * - Transição suave de opacidade no toque (feedback tátil visual)
   *
   * @returns {import('lit').CSSResult} Estilos CSS do botão
   */
  static get modalBaseStyles () {
    return css`
      .modal-base-btn-voltar-ios {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 48px;
        min-height: 48px;
        width: 48px;
        aspect-ratio: 1;
        flex-shrink: 0;
        padding: 0;
        margin: 0;
        border: none;
        background: none;
        color: white;
        font-size: 1.1rem;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition: opacity 150ms ease;
      }

      .modal-base-btn-voltar-ios:active {
        opacity: 0.6;
      }
    `
  }

  /**
   * Callback do Lit executado quando propriedades reativas mudam.
   *
   * Monitora a propriedade `open` para integração com History API:
   * - Quando `open` muda de `false` para `true`: executa `pushState`
   *   para adicionar entrada no histórico, permitindo que o botão voltar
   *   do navegador acione o fechamento via evento `popstate`.
   * - Registra o listener de `popstate` para fechar o modal quando
   *   o usuário navega para trás no histórico.
   *
   * @param {Map<string, any>} changedProperties - Mapa de propriedades alteradas
   */
  updated (changedProperties) {
    super.updated(changedProperties)

    if (changedProperties.has('open')) {
      const oldValue = changedProperties.get('open')

      // Transição false → true: modal abriu
      if (this.open && oldValue === false) {
        this._onModalOpen()
      }
    }
  }

  /**
   * Lógica executada quando o modal abre (open: false → true).
   *
   * Adiciona entrada no history state via pushState para permitir
   * que o evento popstate do navegador acione close(). Registra
   * o listener de popstate se ainda não estiver registrado.
   *
   * Ordem de operações (Req 1.2):
   * 1. pushState com try/catch (Req 1.4 — resiliência a exceção)
   * 2. Setar _historyStatePushed = true ANTES de registrar handler
   * 3. Registrar modal no ModalStackRegistry (Req 7.1)
   * 4. Registrar handler de popstate
   *
   * Se pushState lançar exceção, o modal permanece aberto (open = true)
   * sem integração com History API — degradação graceful.
   */
  _onModalOpen () {
    try {
      window.history.pushState({ modal: true }, '')
    } catch (error) {
      console.warn(
        '[ModalBaseMixin] pushState falhou. Modal permanece aberto sem integração com History API.',
        error
      )
      return
    }

    this._historyStatePushed = true
    modalStackRegistry.register(this)
    this._registerPopstateHandler()
  }

  /**
   * Registra o handler de popstate no window.
   *
   * Cria um handler bound e armazena a referência em _boundPopstateHandler
   * para permitir remoção correta no disconnectedCallback. Se já houver
   * um handler registrado (aberturas subsequentes), reutiliza o existente
   * sem registrar listener duplicado no window (Req 2.4, 4.4).
   *
   * O handler implementa três guards antes de acionar close():
   * 1. _isCleaningHistory — ignora popstate gerado por limpeza de histórico órfão (Req 5.3)
   * 2. this.open — ignora se modal já está fechado (Req 2.3)
   * 3. modalStackRegistry.isTop(this) — apenas o topo da stack responde (Req 7.5)
   *
   * Essa abordagem garante que cada instância de modal tenha no máximo
   * um listener de popstate registrado, independente de quantas vezes
   * o modal é aberto e fechado sem ser desconectado do DOM.
   */
  _registerPopstateHandler () {
    if (this._boundPopstateHandler) return

    this._boundPopstateHandler = () => {
      // Guard 1: Ignora se outro modal já consumiu este popstate no mesmo tick
      // Previne que múltiplos modais respondam ao mesmo evento popstate (Req 2.3, 2.4)
      if (modalStackRegistry.isPopstateConsumed()) return

      // Guard 2: Ignora popstate gerado pela limpeza de histórico órfão (própria ou de outro modal)
      if (this._isCleaningHistory) {
        this._isCleaningHistory = false
        modalStackRegistry._cleanupInProgress = false
        return
      }
      if (modalStackRegistry._cleanupInProgress) {
        modalStackRegistry._cleanupInProgress = false
        return
      }

      // Guard 3: Ignora se modal já está fechado
      if (!this.open) return

      // Guard 4: Apenas o modal do topo da stack responde ao popstate
      if (!modalStackRegistry.isTop(this)) return

      // Marca o popstate como consumido ANTES de fechar
      modalStackRegistry.consumePopstate()

      this.close({ fromPopstate: true })
    }

    window.addEventListener('popstate', this._boundPopstateHandler)
  }

  /**
   * Lifecycle hook de desconexão do DOM.
   *
   * Realiza cleanup completo ao remover o componente do DOM:
   * 1. Remove o listener de popstate do window (Req 4.1) para evitar memory leaks
   *    e chamadas a close() após o componente ser removido
   * 2. Seta _boundPopstateHandler = null (Req 4.2) para liberar referência
   * 3. Reseta _historyStatePushed = false (Req 4.3) para estado limpo
   * 4. Remove o modal do ModalStackRegistry (Req 6.4) caso ainda esteja
   *    registrado (ex: modal removido do DOM enquanto aberto)
   *
   * Após disconnect, eventos popstate subsequentes não acionam close()
   * nesta instância (Req 4.5) pois o listener já foi removido.
   *
   * Garante cleanup correto independente de como o componente foi removido
   * e independente do estado atual (aberto, fechado, com handler ou sem).
   */
  disconnectedCallback () {
    super.disconnectedCallback()

    if (this._boundPopstateHandler) {
      window.removeEventListener('popstate', this._boundPopstateHandler)
      this._boundPopstateHandler = null
    }

    this._historyStatePushed = false

    // Remove da stack caso o modal ainda esteja registrado (Req 6.4)
    // Cenário: modal removido do DOM enquanto aberto (ex: rota muda)
    modalStackRegistry.unregister(this)
  }
}
